// @flow

import addParamsToUrl from './addParamsToUrl';
import getCookie from './getCookie';
import { postJson } from './fetch';

import type { TrackingFunction } from '@zapier/tracking';

export const OAUTH_ACCOUNT_ID_PARAM = '_zapier_account_id';
export const OAUTH_SHARED_PARAM = '_zapier_is_shared';
export const AUTH_ID_PARAM = '_zapier_auth_id';
export const AUTH_DESTINATION_SELECTED_API_PARAM =
  '_zapier_destination_selected_api';
export const AUTH_EXPECTED_IDENTIFIER_PARAM = '_zapier_expected_identifier';
export const AUTH_FOR_SIGNUP_PARAM = 'for_signup';

// Shared param for non-OAuth auths
export const SHARED_AUTH_PARAM = 'isShared';

// Event type that should be emitted (postMessage) from the auth window
export const AUTH_SUCCESS_EVENT_TYPE = 'authenticationSuccess';

// Auth specific errors
export const AUTH_ERROR = 'AUTH_ERROR';
export const AUTH_POPUP_FAILURE = 'AUTH_POPUP_FAILURE';
export const AUTH_FAILED_CONN = 'AUTH_FAILED_CONN';

const POPUP_WINDOW_OPTIONS = 'height=700,width=980,scrollbars=yes';
const POPUP_SPLASH_URL = '/window-splash/';

class ErrorWithCode extends Error {
  code: ?string;
}

const newAuthError = (code = AUTH_ERROR, message): ErrorWithCode => {
  const err = new ErrorWithCode(message);
  err.code = code;
  return err;
};

const connectUrl = ({
  serviceKey,
  accountId,
  isShared,
  authId,
  authIdentifier,
  destinationServiceKey,
  isForSignup,
}) => {
  return addParamsToUrl(
    `/engine/auth/start/${serviceKey}/`,
    {
      [OAUTH_ACCOUNT_ID_PARAM]: accountId,
      [OAUTH_SHARED_PARAM]: isShared,
      [AUTH_ID_PARAM]: authId,
      [AUTH_EXPECTED_IDENTIFIER_PARAM]: authIdentifier,
      [AUTH_DESTINATION_SELECTED_API_PARAM]: destinationServiceKey,
      [AUTH_FOR_SIGNUP_PARAM]: isForSignup,
    },
    true
  );
};

const fetchConnectUrl = params =>
  postJson(connectUrl(params)).then(({ url }: { url: string }) => url);

type AuthConnectionMessage = {
  type: string,
  authId: string,
};

// Message creator to post from authentication window on successful connection.
export const createAuthConnectionMessage = (
  authId: string
): AuthConnectionMessage => ({
  type: AUTH_SUCCESS_EVENT_TYPE,
  authId,
});

// Takes `win` popup window that authorizes a connection.
// Returns a `Promise` that will `resolve` if an
// `authId` is provided by `win` or `reject` if no
// `authId` is provided. `win` provides `authId` via
// a `postMessage` event to our `window`. We `poll`
// until `win` is `closed`. Would be nice to make that
// event based using `unload` or something, but not
// having a lot of luck with that.
const waitForConnection = (win, isOauth): Promise<?string> => {
  return new Promise((resolve, reject) => {
    const { origin } = window.location;
    let authId;
    let success = false;

    const onMessage = e => {
      if (e.origin === origin && e.data.type === AUTH_SUCCESS_EVENT_TYPE) {
        authId = e.data.authId;
        success = true;
      }
    };
    window.addEventListener('message', onMessage);

    const poll = () => {
      if (win.closed) {
        // If the parent window loses focus, then we'll detect the window is
        // closed before we receive our message. Wait for the next event turn
        // to make sure all messages are flushed.
        setTimeout(() => {
          window.removeEventListener('message', onMessage);

          // oauth will not always return auth id
          if (!success || (authId == null && !isOauth)) {
            reject(
              newAuthError(AUTH_FAILED_CONN, 'Failed to connect account.')
            );
          }

          resolve(authId);
        }, 0);
      } else {
        setTimeout(poll, 1000);
      }
    };
    poll();
  });
};

// Decorate the original url with a /csrf/fix view/url that will set the missing csrf
// token. This is a useful workaround for Safari's security policy that block cookies
// in iframes if the user has never visited the site outside of an iframe.
//
// Note: The backend requires the `next` url to be strictly the POPUP_SPLASH_URL based
// on security concerns.
const maybeCsrfFix = url =>
  !getCookie('csrftoken') ? addParamsToUrl('/csrf/fix/', { next: url }) : url;

// Create the popup window and return the new window object ref.
// If the window cannot be created (is blocked) then null is returned.
// See https://developer.mozilla.org/en-US/docs/Web/API/Window/open#Return_value
const createPopupWindow = url =>
  window.open(url, '_blank', POPUP_WINDOW_OPTIONS);

const createPopupSplash = () =>
  new Promise((resolve, reject) => {
    const { origin } = window.location;
    const popup = createPopupWindow(maybeCsrfFix(POPUP_SPLASH_URL));

    // When popups are blocked by the browser `popup` will be falsey
    // so we return early here.
    if (!popup) {
      reject(newAuthError(AUTH_POPUP_FAILURE, 'Popup was blocked.'));
      return;
    }

    // This code will setup a postMessage listener that will listen for an event
    // with the data payload of `ready`. Assuming that the origins match, this
    // will then trigger the resolve of the `popup` which will eventually be used
    // to redirect the popup to the desired authentication URL.
    // This code exists for cross-browser purposes, binding to the `popup`
    // `DOMContentLoaded` or `load` event will not work in all browsers.
    const onPostMessageAuthPopUpReady = event => {
      if (event.origin === origin && event.data === 'ready') {
        window.removeEventListener('message', onPostMessageAuthPopUpReady);

        resolve(popup);
      }
    };

    window.addEventListener('message', onPostMessageAuthPopUpReady, false);
  });

const safelyAddSharedAuthParamsToUrl = (
  url,
  params,
  isOauth,
  hasUpfrontFields
) => {
  // It's only safe to add params to the URL if the auth URL is internal.
  // Tacking on params to an external url like an OAuth authorize URL might get
  // rejected by the partner app.
  // We still need to add the params if there are upfront fields, or the auth
  // won't initialize properly.
  const isSafeToAddParamsToUrl = !isOauth || hasUpfrontFields;
  return !isSafeToAddParamsToUrl
    ? url
    : addParamsToUrl(
        url,
        {
          [SHARED_AUTH_PARAM]: params.isShared,
          [AUTH_ID_PARAM]: params.authId,
        },
        true
      );
};

type ConnectToAuthParams = {
  serviceKey: string,
  accountId?: string,
  hasUpfrontFields?: boolean,
  isShared?: boolean,
  isOauth?: boolean,
  authId?: string,
  authIdentifier?: string,
  destinationServiceKey?: string,
  onError?: (e: Error) => void,
  onTrack?: TrackingFunction,
  isForSignup?: boolean,
};

/**
 * This function will open a new connect auth window. If it is possible, this
 * function will first open a splash (loading) screen and then redirect to the
 * desired auth configuration, or it will immediately open the auth config.
 */
export const connectToAuth = async ({
  accountId,
  hasUpfrontFields,
  isShared,
  isOauth,
  serviceKey,
  authId,
  authIdentifier, // Require created auth to have this identity.
  destinationServiceKey,
  onError = () => {},
  onTrack = (() => Promise.resolve(): TrackingFunction),
  isForSignup,
}: ConnectToAuthParams): Promise<?string> => {
  const params = {
    accountId,
    isShared,
    serviceKey,
    authId,
    authIdentifier,
    destinationServiceKey,
    isForSignup,
  };

  // Create the splash popup if possible, then retrieve the connect URL
  // to ensure it is valid. We do these as separate steps because we want the
  // popup window to open ASAP rather than waiting for the `fetchConnectUrl` to
  // resolve.
  const win = await createPopupSplash();
  const url = await fetchConnectUrl(params);
  const parameterizedUrl = safelyAddSharedAuthParamsToUrl(
    url,
    params,
    isOauth,
    hasUpfrontFields
  );

  onTrack('auth: redirect to first leg', {
    accountId,
    authId,
    isForSignup,
    isOauth,
    parameterizedUrl,
    serviceKey,
  });

  try {
    win.location = parameterizedUrl;
  } catch (e) {
    onError(e);
    throw newAuthError(
      AUTH_POPUP_FAILURE,
      'Could not change the popup location.'
    );
  }

  try {
    const conn = await waitForConnection(win, isOauth);

    onTrack('auth: connection success', {
      accountId,
      authId,
      conn,
      isForSignup,
      isOauth,
      parameterizedUrl,
      serviceKey,
    });

    return conn;
  } catch (e) {
    onTrack('auth: could not get connection', {
      accountId,
      authId,
      error: e.message,
      isForSignup,
      isOauth,
      parameterizedUrl,
      serviceKey,
    });

    throw e;
  }
};

export const connectGoogleSuiteAuths = () => {
  const popupWindow = createPopupWindow('/google-sso/start?scopes=multi');
  return waitForConnection(popupWindow, true);
};
